import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { PageHeader, Spinner, EmptyState, useToast, InlineSpinner } from '../components/UI';
import { fmtDate, fmtPct } from '../utils/format';

// Three-tier read on a rate instead of a flat pass/fail badge — gives the
// grid some life and makes "barely passing" visually distinct from "solid".
const rateColor = rate => rate == null ? 'var(--sms-txt3)' : rate >= 70 ? 'var(--sms-stat-green)' : rate >= 50 ? 'var(--sms-stat-amber)' : 'var(--sms-stat-red)';

function SortBar({ sortKey, sortDir, onSort, fields, search, onSearch, placeholder }) {
  return (
    <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
      <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 180 }}>
        <i className="bi bi-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--sms-txt3)', fontSize: 13 }} />
        <input className="sms-form-control" style={{ paddingLeft: 32 }} placeholder={placeholder} value={search}
          onChange={e => onSearch(e.target.value)} />
      </div>
      <div className="d-flex gap-1">
        {fields.map(([key, label]) => {
          const active = sortKey === key;
          const iconClass = active ? (sortDir === 'asc' ? 'bi-sort-up' : 'bi-sort-down') : 'bi-arrow-down-up';
          return (
            <button key={key} type="button" onClick={() => onSort(key)}
              className={`sms-btn sms-sort-btn ${active ? 'sms-btn-primary' : 'sms-btn-outline'}`}
              style={{ fontSize: 12, padding: '6px 10px' }}>
              {label}
              <i className={`bi ${iconClass} sms-sort-icon`} style={{ marginLeft: 5 }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RateCard({ icon, title, subtitle, total, pass, rate, onClick }) {
  const color = rateColor(rate);
  const [pressed, setPressed] = useState(false);

  // Play the press animation first, then hand off to the real click — since
  // drilling in swaps the whole grid out immediately, firing onClick straight
  // away would cut the animation off before it's visible.
  const handleClick = () => {
    if (pressed) return;
    setPressed(true);
    setTimeout(onClick, 150);
  };

  return (
    <div className="col-12 col-sm-6 col-lg-4">
      <div className={`sms-card sms-rate-card${pressed ? ' sms-rate-card-pressed' : ''}`} role="button" tabIndex={0}
        style={{ padding: '14px 16px', cursor: 'pointer', height: '100%', '--card-accent': color }}
        onClick={handleClick} onKeyDown={e => e.key === 'Enter' && handleClick()}>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
            <div className="sms-rate-icon" style={{ width: 32, height: 32, flex: '0 0 auto', borderRadius: 9, background: `color-mix(in srgb, ${color} 14%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className={`bi ${icon}`} style={{ color, fontSize: 15 }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
              {subtitle && <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>{subtitle}</div>}
            </div>
          </div>
          <i className="bi bi-chevron-right" style={{ color: 'var(--sms-txt3)', flex: '0 0 auto' }} />
        </div>
        {total > 0 ? (
          <>
            <div className="d-flex justify-content-between align-items-baseline">
              <span style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>{pass} / {total} passed</span>
              <span style={{ fontSize: 17, fontWeight: 700, color }}>{fmtPct(rate)}</span>
            </div>
            <div className="sms-progress"><div className="sms-progress-fill" style={{ width: `${rate}%`, background: color }} /></div>
          </>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginTop: 4 }}>No fully-marked students yet</div>
        )}
      </div>
    </div>
  );
}

export default function ClassPassingRatePage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [options, setOptions] = useState({ courses: [], intakes: [] });
  const [loading, setLoading] = useState(true);
  // Seeded from ?course=&intake= (e.g. the dashboard's "Class Passing Rate"
  // card links here pointing at its top-performing class) so the page opens
  // already drilled down instead of always landing on "All courses".
  const [course, setCourse] = useState(() => searchParams.get('course') || null);
  const [intake, setIntake] = useState(() => searchParams.get('intake') || null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'rate', dir: 'asc' });
  const [downloading, setDownloading] = useState(null); // 'pdf' | 'xlsx' | null

  useEffect(() => {
    Promise.all([
      api.get('/marks-review/stats'),
      api.get('/reports/passing-rate/options'),
    ]).then(([statsRes, optRes]) => {
      setStats(statsRes.data.data);
      setOptions(optRes.data.data);
    }).catch(() => toast('Failed to load passing rate data', 'error'))
      .finally(() => setLoading(false));
  }, []);

  // Reset search + sort when the drill-down level changes so a leftover query
  // from "search courses" doesn't silently hide every intake underneath.
  const drillTo = (nextCourse, nextIntake) => {
    setCourse(nextCourse); setIntake(nextIntake);
    setSearch(''); setSort({ key: 'rate', dir: 'asc' });
  };

  const toggleSort = key => setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  const rows = stats?.rows || [];
  const byCourseRows = course ? rows.filter(r => r.course_code === course) : rows;
  const byIntakeRows = intake ? byCourseRows.filter(r => r.intake_code === intake) : byCourseRows;
  const scopeRows = intake ? byIntakeRows : byCourseRows;

  const overallRate = scopeRows.length ? Math.round((scopeRows.filter(r => r.result === 'pass').length / scopeRows.length) * 100) : null;
  const overallPass = scopeRows.filter(r => r.result === 'pass').length;

  const rateOf = (pass, total) => total ? Math.round((pass / total) * 100) : null;

  // Every active course, not just ones with marked exams — a course sitting
  // at 0 fully-marked students is exactly the kind of gap worth surfacing,
  // not silently dropping from the list.
  const byCourse = useMemo(() => {
    const withData = {};
    for (const r of rows) {
      if (!r.course_code) continue;
      if (!withData[r.course_code]) withData[r.course_code] = { total: 0, pass: 0 };
      withData[r.course_code].total++;
      if (r.result === 'pass') withData[r.course_code].pass++;
    }
    return options.courses.map(c => {
      const d = withData[c.course_code] || { total: 0, pass: 0 };
      return { course_code: c.course_code, course_name: c.course_name, total: d.total, pass: d.pass, rate: rateOf(d.pass, d.total) };
    });
  }, [rows, options.courses]);

  const byIntake = useMemo(() => {
    const withData = {};
    for (const r of byCourseRows) {
      if (!withData[r.intake_code]) withData[r.intake_code] = { total: 0, pass: 0 };
      withData[r.intake_code].total++;
      if (r.result === 'pass') withData[r.intake_code].pass++;
    }
    const intakesForCourse = course ? options.intakes.filter(i => i.course_code === course) : options.intakes;
    return intakesForCourse.map(i => {
      const d = withData[i.intake_code] || { total: 0, pass: 0 };
      return { intake_code: i.intake_code, soc_date: i.soc_date, eoc_date: i.eoc_date, total: d.total, pass: d.pass, rate: rateOf(d.pass, d.total) };
    });
  }, [byCourseRows, options.intakes, course]);

  // Finest cut — each class (intake + module + exam type) only exists once a
  // scheme has actually produced marks, so this stays sourced from stats.rows.
  const byClass = useMemo(() => Object.values(
    byIntakeRows.reduce((acc, r) => {
      const key = `${r.intake_code}|${r.module_code}|${r.exam_type}`;
      if (!acc[key]) acc[key] = { intake_code: r.intake_code, module_code: r.module_code, module_name: r.module_name, exam_type: r.exam_type, total: 0, pass: 0 };
      acc[key].total++;
      if (r.result === 'pass') acc[key].pass++;
      return acc;
    }, {})
  ).map(c => ({ ...c, rate: rateOf(c.pass, c.total) })), [byIntakeRows]);

  const applySearchSort = (list, nameField, codeField) => {
    const q = search.trim().toLowerCase();
    const filtered = q ? list.filter(c => (c[nameField] || '').toLowerCase().includes(q) || (codeField && (c[codeField] || '').toLowerCase().includes(q))) : list;
    return [...filtered].sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  };

  const goToRoster = c => navigate(`/records/marks-review?intake_code=${c.intake_code}&module_code=${c.module_code}&exam_type=${c.exam_type}`);

  // Export is per-intake — the backend report it hits is grouped by module
  // for a single intake, same shape as the class-level grid below it.
  const download = async format => {
    setDownloading(format);
    try {
      const path = format === 'pdf' ? '/reports/passing-rate/export-pdf' : '/reports/passing-rate/export';
      const res = await api.get(path, { params: { intake_code: intake }, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = `passing_rate_${intake}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
    } catch {
      toast('Download failed', 'error');
    } finally {
      setDownloading(null);
    }
  };

  const courseName = course ? (options.courses.find(c => c.course_code === course)?.course_name || course) : null;
  const noCoursesAtAll = !loading && options.courses.length === 0;

  return (
    <>
      <PageHeader title="Class Passing Rate" subtitle="Passed students ÷ fully-marked students, drilled down from every course to a single class" />

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2" style={{ marginBottom: 14 }}>
        <div className="d-flex flex-wrap align-items-center gap-2">
          {(course || intake) && (
            <button type="button" className="sms-btn sms-btn-outline sms-back-btn" style={{ fontSize: 12, padding: '5px 10px' }}
              onClick={() => intake ? drillTo(course, null) : drillTo(null, null)}>
              <i className="bi bi-arrow-left sms-back-arrow" /> Back
            </button>
          )}
          <div style={{ display: 'flex', gap: 6, fontSize: 12, color: 'var(--sms-txt2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <span role="button" tabIndex={0} style={{ color: course ? 'var(--sms-acc)' : 'var(--sms-txt)', fontWeight: course ? 400 : 600, cursor: 'pointer' }}
              onClick={() => drillTo(null, null)}>All courses</span>
            {course && (
              <>
                <i className="bi bi-chevron-right" style={{ fontSize: 12 }} />
                <span role="button" tabIndex={0} style={{ color: intake ? 'var(--sms-acc)' : 'var(--sms-txt)', fontWeight: intake ? 400 : 600, cursor: 'pointer' }}
                  onClick={() => drillTo(course, null)}>{courseName}</span>
              </>
            )}
            {intake && (
              <>
                <i className="bi bi-chevron-right" style={{ fontSize: 12 }} />
                <span style={{ color: 'var(--sms-txt)', fontWeight: 600 }}>{intake}</span>
              </>
            )}
          </div>
        </div>
        {intake && byClass.length > 0 && (
          <div className="d-flex gap-2">
            <button type="button" className="sms-btn sms-btn-outline sms-btn-lively" style={{ fontSize: 12, padding: '6px 10px' }}
              disabled={!!downloading} onClick={() => download('xlsx')}>
              {downloading === 'xlsx' ? <InlineSpinner /> : <i className="bi bi-file-earmark-excel" />} Excel
            </button>
            <button type="button" className="sms-btn sms-btn-primary sms-btn-lively" style={{ fontSize: 12, padding: '6px 10px' }}
              disabled={!!downloading} onClick={() => download('pdf')}>
              {downloading === 'pdf' ? <InlineSpinner /> : <i className="bi bi-file-earmark-pdf" />} PDF
            </button>
          </div>
        )}
      </div>

      {loading ? <Spinner minHeight="240px" /> : noCoursesAtAll ? (
        <EmptyState icon="bi-graph-up-arrow" message="No active courses yet" />
      ) : (
        <>
          <div className="row g-2 mb-3">
            {[
              ['Pass rate', overallRate != null ? `${fmtPct(overallRate)}` : '—'],
              ['Passed', `${overallPass} / ${scopeRows.length}`],
            ].map(([lbl, value]) => (
              <div className="col-6 col-md-3" key={lbl}>
                <div className="sms-card" style={{ padding: '12px 14px' }}>
                  <div style={{ fontSize: 12, color: 'var(--sms-txt2)', marginBottom: 4 }}>{lbl}</div>
                  <div style={{ fontSize: 20, fontWeight: 600 }}>{value}</div>
                </div>
              </div>
            ))}
          </div>

          {!course ? (
            <>
              <SortBar sortKey={sort.key} sortDir={sort.dir} onSort={toggleSort} search={search} onSearch={setSearch}
                placeholder="Search courses…" fields={[['course_name', 'Name'], ['rate', 'Pass rate'], ['total', 'Students']]} />
              {applySearchSort(byCourse, 'course_name', 'course_code').length === 0 ? (
                <EmptyState icon="bi-search" message="No courses match your search" />
              ) : (
                <div className="row g-2" key="course-level" style={{ animation: 'sms-slide-in .25s ease both' }}>
                  {applySearchSort(byCourse, 'course_name', 'course_code').map(c => (
                    <RateCard key={c.course_code} icon="bi-mortarboard-fill" title={c.course_name} subtitle={c.course_code}
                      total={c.total} pass={c.pass} rate={c.rate} onClick={() => drillTo(c.course_code, null)} />
                  ))}
                </div>
              )}
            </>
          ) : !intake ? (
            <>
              <SortBar sortKey={sort.key} sortDir={sort.dir} onSort={toggleSort} search={search} onSearch={setSearch}
                placeholder="Search intakes…" fields={[['intake_code', 'Name'], ['rate', 'Pass rate'], ['total', 'Students']]} />
              {applySearchSort(byIntake, 'intake_code').length === 0 ? (
                <EmptyState icon="bi-search" message="No intakes match your search" />
              ) : (
                <div className="row g-2" key={`intake-level-${course}`} style={{ animation: 'sms-slide-in .25s ease both' }}>
                  {applySearchSort(byIntake, 'intake_code').map(c => (
                    <RateCard key={c.intake_code} icon="bi-calendar-event" title={c.intake_code}
                      subtitle={c.soc_date ? `${fmtDate(c.soc_date)} – ${c.eoc_date ? fmtDate(c.eoc_date) : '—'}` : null}
                      total={c.total} pass={c.pass} rate={c.rate} onClick={() => drillTo(course, c.intake_code)} />
                  ))}
                </div>
              )}
            </>
          ) : byClass.length === 0 ? (
            <EmptyState icon="bi-inbox" message="No fully-marked students yet for this intake" />
          ) : (
            <>
              <SortBar sortKey={sort.key} sortDir={sort.dir} onSort={toggleSort} search={search} onSearch={setSearch}
                placeholder="Search modules…" fields={[['module_name', 'Name'], ['rate', 'Pass rate'], ['total', 'Students']]} />
              <div className="row g-2" key={`class-level-${intake}`} style={{ animation: 'sms-slide-in .25s ease both' }}>
                {applySearchSort(byClass, 'module_name').map(c => (
                  <RateCard key={`${c.module_code}:${c.exam_type}`} icon="bi-journal-bookmark-fill" title={c.module_name}
                    subtitle={c.exam_type[0].toUpperCase() + c.exam_type.slice(1)}
                    total={c.total} pass={c.pass} rate={c.rate} onClick={() => goToRoster(c)} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}
